<template>
  <div class="image-upload">
    <el-upload
      ref="uploader"
      :action="uploadUrl"
      :headers="uploadHeaders"
      :data="{ folderName }"
      list-type="picture-card"
      :file-list="fileList"
      :limit="limit"
      :multiple="limit > 1"
      :before-upload="handleBeforeUpload"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :on-exceed="handleExceed"
      :class="{ 'hide-add-btn': fileList.length >= limit }"
      :style="uploadStyle"
      accept="image/*"
    >
      <div class="upload-trigger">
        <i v-if="!uploading" class="el-icon-plus" />
        <i v-else class="el-icon-loading" />
      </div>
    </el-upload>

    <!-- 提示文字 -->
    <div v-if="showTip" class="upload-tip">
      <span v-if="fileType && fileType.length">
        支持 <b>{{ fileType.join(' / ').toUpperCase() }}</b> 格式
      </span>
      <span v-if="fileSize">，大小不超过 <b>{{ fileSize }} MB</b></span>
      <span v-if="limit > 1">，最多上传 <b>{{ limit }}</b> 张</span>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="680px"
      append-to-body
      :close-on-click-modal="true"
    >
      <img :src="previewUrl" class="preview-img" />
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ImageUpload',

  props: {
    value: {
      type: [String, Array],
      default: ''
    },
    folderName: {
      type: String,
      default: 'image'
    },
    limit: {
      type: Number,
      default: 1
    },
    fileSize: {
      type: Number,
      default: 5
    },
    fileType: {
      type: Array,
      default: () => ['jpg', 'jpeg', 'png', 'gif', 'webp']
    },
    isShowTip: {
      type: Boolean,
      default: true
    },
    width: {
      type: [Number, String],
      default: 100
    },
    height: {
      type: [Number, String],
      default: 100
    }
  },

  data() {
    return {
      // 我们自己维护的已确认上传成功的 URL 列表（不含上传中的临时条目）
      fileList: [],
      uploading: false,
      pendingCount: 0,
      previewVisible: false,
      previewUrl: '',
      // 防止 emitChange -> value watch 互相触发
      emitting: false
    };
  },

  computed: {
    uploadUrl() {
      const base = process.env.VUE_APP_BASE_API || '/api';
      return `${base}/common/txcos/upload`;
    },
    uploadHeaders() {
      const token = localStorage.getItem('MIAOBI_TOKEN');
      return token ? { satoken: token } : {};
    },
    uploadStyle() {
      const w = typeof this.width === 'number' ? `${this.width}px` : this.width;
      const h = typeof this.height === 'number' ? `${this.height}px` : this.height;
      return { '--upload-w': w, '--upload-h': h };
    },
    showTip() {
      return this.isShowTip && (this.fileType.length || this.fileSize);
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        // emitting 期间不响应，避免自己 emit 触发 watch 回写
        if (this.emitting) return;

        const urls = !val
          ? []
          : Array.isArray(val)
            ? val.filter(Boolean)
            : val.split(',').filter(Boolean);

        // 与当前一致则不重置，防止上传中被打断
        const current = this.fileList.map(f => f.url).join(',');
        if (current === urls.join(',')) return;

        this.fileList = urls.map(url => ({ name: url, url }));
      }
    }
  },

  methods: {
    handleBeforeUpload(file) {
      const ext = file.name.split('.').pop().toLowerCase();
      const typeOk = this.fileType.length === 0
        || this.fileType.some(t => t.toLowerCase() === ext || file.type.includes(t));
      if (!typeOk) {
        this.$message.error(`仅支持 ${this.fileType.join(' / ').toUpperCase()} 格式的图片`);
        return false;
      }
      if (this.fileSize && file.size / 1024 / 1024 > this.fileSize) {
        this.$message.error(`图片大小不能超过 ${this.fileSize} MB`);
        return false;
      }
      this.uploading = true;
      this.pendingCount++;
      return true;
    },

    handleSuccess(res, file) {
      this.pendingCount = Math.max(0, this.pendingCount - 1);
      if (this.pendingCount === 0) this.uploading = false;

      if (res && res.code === 200 && res.url) {
        // 直接把 COS URL 追加到我们自己维护的 fileList
        // （el-upload 内部已经把 file 加入了它自己的 uploadFiles，这里我们只管 fileList）
        this.fileList = [
          ...this.fileList.filter(f => f.url && f.url.startsWith('http')),
          { name: res.url, url: res.url }
        ];
        // limit=1 时只保留最新一张
        if (this.limit === 1) {
          this.fileList = [{ name: res.url, url: res.url }];
          // 同步清掉 el-upload 内部多余的条目，保留最新
          const uploader = this.$refs.uploader;
          if (uploader && uploader.uploadFiles) {
            uploader.uploadFiles.splice(0, uploader.uploadFiles.length - 1);
          }
        }
        this.$message.success('上传成功');
        this.emitChange();
      } else {
        this.$message.error(res && res.msg ? res.msg : '上传失败，请重试');
        this.$refs.uploader && this.$refs.uploader.handleRemove(file);
      }
    },

    handleError(err) {
      this.pendingCount = Math.max(0, this.pendingCount - 1);
      if (this.pendingCount === 0) this.uploading = false;
      this.$message.error('上传失败，请检查网络后重试');
      console.error('[ImageUpload] upload error:', err);
    },

    handleRemove(file) {
      // 从 fileList 中移除被删除的条目
      this.fileList = this.fileList.filter(
        f => f.url !== file.url && f.name !== file.name
      );
      this.emitChange();
    },

    handleExceed() {
      this.$message.warning(`最多只能上传 ${this.limit} 张图片`);
    },

    handlePreview(file) {
      this.previewUrl = file.url;
      this.previewVisible = true;
    },

    emitChange() {
      const urls = this.fileList
        .map(f => f.url)
        .filter(url => url && url.startsWith('http'));

      this.emitting = true;
      this.$emit('input', this.limit === 1 ? (urls[0] || '') : urls.join(','));
      this.$nextTick(() => { this.emitting = false; });
    }
  }
};
</script>

<style lang="scss" scoped>
.image-upload {
  display: inline-block;
}

.image-upload ::v-deep {
  .el-upload--picture-card,
  .el-upload-list--picture-card .el-upload-list__item {
    width: var(--upload-w, 100px);
    height: var(--upload-h, 100px);
    line-height: var(--upload-h, 100px);
  }
}

.image-upload.hide-add-btn ::v-deep .el-upload--picture-card,
.image-upload ::v-deep .hide-add-btn .el-upload--picture-card {
  display: none;
}

.upload-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 24px;
  color: #8c939d;
}

.upload-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;

  b {
    font-weight: 600;
    color: #606266;
  }
}

.preview-img {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: 4px;
}

.image-upload ::v-deep .el-list-enter-active,
.image-upload ::v-deep .el-list-leave-active {
  transition: none;
}
</style>
